import cn from 'classnames';
import { Scrollbar } from 'react-scrollbars-custom';
import { ReactNode, useCallback, useId, useMemo, useState } from 'react';
import { getAxisValues } from '@core/utils/chart';
import { AutoSizer } from 'react-virtualized';
import { FCX, ID, Tooltip } from "@datassential/platform-ui-lib";
import { IOption } from "@models";
import Curves from "@components/GroupsColumnsLineChart/Curves";
import Popup from "@components/Popup";

export type ChartValue = number | null;

export type Series = {
    id: ID;
    title: ReactNode;
    color: string;
    values: Array<{
        groupId: ID;
        columnId: ID;
        value: ChartValue;
    }>;
}

const isNumber = (value: unknown) => typeof value === 'number';

const GroupsColumnsLineChart: FCX<{
    series: Series[];
    groups: IOption[];
    columns: IOption[];
    renderValue: (value: ChartValue) => ReactNode;
    renderTooltipTitle?: (groupTitle: string, columnTitle: string) => ReactNode;
    activeSeriesId?: ID;
}> = ({
    series,
    groups: rawGroups,
    columns: rawColumns,
    activeSeriesId = null,
    renderValue,
    renderTooltipTitle = (groupTitle, columnTitle) => `${groupTitle} ${columnTitle}`,
    className,
    testId,
    style,
    ...restProps
}) => {
    const id = useId();
    const [scrollLeft, setScrollLeft] = useState(0);
    const [activeGroupId, setActiveGroupId] = useState<ID>(null);
    const [activeColumnId, setActiveColumnId] = useState<ID>(null);

    const allValues = useMemo(
        (): number[] => {
            const result = series.flatMap(
                s => s.values.map(v => v.value)
            );

            return result.filter(v => v !== null) as unknown as number[];
        },
        [series]
    );

    const groups = useMemo(
        () => [...rawGroups].reverse(),
        [rawGroups]
    );

    const columns = useMemo(
        () => [...rawColumns].reverse(),
        [rawColumns]
    );

    const maxValue = useMemo(
        () => {
            return allValues.length ? Math.max(...allValues.filter(isNumber)) : 0;
        },
        [allValues]
    );

    const minValue = useMemo(
        () => {
            return allValues.length ? Math.min(...allValues.filter(isNumber)) : 0;
        },
        [allValues]
    );

    const labels = useMemo(
        () => {
            return getAxisValues(maxValue, minValue);
        },
        [maxValue, minValue]
    );

    const getTooltipValue = useCallback(
        (seriesId: ID, groupId: ID, columnId: ID): ChartValue => {
            const seriesItem = series.find(s => s.id === seriesId);

            const valueItem = seriesItem?.values.find(
                v => v.groupId === groupId && v.columnId === columnId
            );

            return valueItem?.value ?? null;
        },
        [series]
    );

    const activeGroup = useMemo(
        () => groups.find(g => g.id === activeGroupId),
        [activeGroupId, groups]
    );

    const activeColumn = useMemo(
        () => columns.find(c => c.id === activeColumnId),
        [activeColumnId, columns]
    );

    const tooltipData = useMemo(
        () => {
                return series
                    .map(s => ({
                        id: s.id,
                        title: s.title,
                        color: s.color,
                        value: getTooltipValue(s.id, activeGroup?.id, activeColumn?.id),
                    }))
                    .sort((a, b) => {
                        if (b.value === null) return -1;
                        if (a.value === null) return 1;
                        return b.value - a.value;
                    });
        },
        [series, activeGroup, activeColumn, getTooltipValue]
    );

    return (
        <div
            className={cn("GroupsColumnsLineChart", className,)}
            style={style}
            data-testid={testId}
            {...restProps}
        >
            <div className={cn(
                "GroupsColumnsLineChart__viewport",
            )}>
                <div className="GroupsColumnsLineChart__y-axis">
                    {labels.map((label, index) => (
                        <div
                            key={label}
                            className="GroupsColumnsLineChart__y-axis-label"
                            data-testid={`textLineChartYAxisLabel_${index}`}
                        >
                            {renderValue(label)}
                        </div>
                    ))}
                </div>
                <Scrollbar
                    onScroll={(data) => {
                        // @ts-ignore
                        setScrollLeft(data.scrollLeft);
                    }}
                    scrollLeft={scrollLeft}
                    rtl
                    noScrollY
                    trackXProps={{
                        renderer: ({ elementRef, style, ...restProps }) => (
                            <div
                                {...restProps}
                                ref={elementRef}
                                className="GroupsColumnsLineChart__scrollbar"
                                style={{
                                    ...style,
                                    width: '100%',
                                    left: 0,
                                }}
                            />
                        ),
                    }}
                >
                    <div className="GroupsColumnsLineChart__view">
                        <div className="GroupsColumnsLineChart__groups">
                            <div className="GroupsColumnsLineChart__curves">
                                <AutoSizer>
                                    {({ width, height }) => (
                                        <Curves
                                            series={series}
                                            groups={groups}
                                            columns={columns}
                                            labels={labels}
                                            height={height}
                                            width={width}
                                            activeSeriesId={activeSeriesId}
                                            activeColumnId={activeColumnId}
                                            activeGroupId={activeGroupId}
                                        />
                                    )}
                                </AutoSizer>
                            </div>
                            {groups.map((g, index) => (
                                <div
                                    key={g.id}
                                    className="GroupsColumnsLineChart__group"
                                    data-testid={`blockLineChartGroup_${index}`}
                                >
                                    <div className="GroupsColumnsLineChart__columns">
                                        {columns.map((c, cIndex) => (
                                            <div
                                                key={c.id}
                                                className="GroupsColumnsLineChart__bar"
                                            >
                                                <div
                                                    className="GroupsColumnsLineChart__bar-inner"
                                                    data-tooltip-id={id}
                                                    onMouseEnter={() => {
                                                        setActiveGroupId(g.id);
                                                        setActiveColumnId(c.id);
                                                    }}
                                                    onMouseLeave={() => {
                                                        setActiveGroupId(null);
                                                        setActiveColumnId(null);
                                                    }}
                                                />
                                                <div
                                                    className="GroupsColumnsLineChart__bar-label"
                                                    data-testid={`textLineChartBarLabel_${index}_${cIndex}`}
                                                >
                                                    {c.title?.substring(0, 3)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div
                                        className="GroupsColumnsLineChart__group-label"
                                        data-testid={`textLineChartGroupLabel_${index}`}
                                    >
                                        {g.title}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Scrollbar>
                <Popup>
                    <Tooltip
                        id={id}
                        place="right"
                        float
                        style={{
                            animation: 'none',
                            transition: "none",
                        }}
                    >
                        <div className="GroupsColumnsLineChart__tooltip">
                            <div
                                className="GroupsColumnsLineChart__tooltip-title"
                                data-testid="textLineChartTooltipTitle"
                            >
                                {renderTooltipTitle(activeGroup?.title ?? '', activeColumn?.title ?? '')}
                            </div>
                            {tooltipData.map((s, index) => (
                                <div
                                    key={s.id}
                                    className={cn(
                                        "GroupsColumnsLineChart__tooltip-row",
                                        activeSeriesId !== null && activeSeriesId !== s.id && 'is-dimmed',
                                        activeSeriesId === s.id && 'is-active',
                                    )}
                                    data-testid={`blockLineChartTooltipRow_${index}`}
                                >
                                    <div data-testid={`textLineChartTooltipRowTitle_${index}`}>{s.title}</div>
                                    <div
                                        className="GroupsColumnsLineChart__tooltip-row-color"
                                        style={{ background: s.color }}
                                        data-testid={`blockLineChartTooltipRowColor_${index}`}
                                    />
                                    <div className="GroupsColumnsLineChart__tooltip-row-value">
                                        {renderValue(s.value)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Tooltip>
                </Popup>
            </div>
        </div>
    );
};

export default GroupsColumnsLineChart;
